import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getWorkflowRun,
  getWorkflow,
  getWorkflowRunState,
  stepWorkflowRun,
  continueWorkflowRun,
  abortWorkflowRun,
  type WorkflowRun,
  type Workflow,
  type WorkflowRunState,
} from "../api/client.js";

const STATE_STYLES: Record<string, { badge: string; label: string }> = {
  pending: { badge: "badge-gray", label: "Pending" },
  running: { badge: "badge-blue", label: "Running" },
  completed: { badge: "badge-green", label: "Completed" },
  failed: { badge: "badge-red", label: "Failed" },
  paused: { badge: "badge-amber", label: "Paused" },
  cancelled: { badge: "badge-gray", label: "Cancelled" },
};

const NODE_STATE_COLORS: Record<string, string> = {
  completed: "bg-emerald-100 border-emerald-300 text-emerald-700",
  failed: "bg-red-100 border-red-300 text-red-700",
  skipped: "bg-ink-100 border-ink-200 text-ink-400",
  running: "bg-blue-100 border-blue-300 text-blue-700",
  pending: "bg-ink-50 border-ink-200 text-ink-500",
  waiting_approval: "bg-amber-100 border-amber-300 text-amber-700",
};

export function RunViewer() {
  const { workflowId, runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState<WorkflowRun | null>(null);
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [runState, setRunState] = useState<WorkflowRunState | null>(null);
  const [loading, setLoading] = useState(true);
  const [stepping, setStepping] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  useEffect(() => {
    if (!workflowId || !runId) return;

    Promise.all([
      getWorkflowRun(workflowId, runId),
      getWorkflow(workflowId),
    ])
      .then(([r, w]) => {
        setRun(r);
        setWorkflow(w);
        // Fetch full debug state if debug run
        if (r.debugState) {
          getWorkflowRunState(workflowId, runId)
            .then(setRunState)
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workflowId, runId]);

  // Poll while running or paused (debug mode)
  useEffect(() => {
    if (!run || !workflowId || !runId) return;
    if (run.state !== "running" && run.state !== "pending" && run.state !== "paused") return;

    const interval = setInterval(async () => {
      try {
        const updated = await getWorkflowRun(workflowId, runId);
        setRun(updated);
        if (updated.debugState || updated.nodeStates) {
          const state = await getWorkflowRunState(workflowId, runId);
          setRunState(state);
        }
        if (updated.state !== "running" && updated.state !== "pending" && updated.state !== "paused") {
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [run?.state, workflowId, runId]);

  const handleStep = async () => {
    if (!workflowId || !runId) return;
    setStepping(true);
    try {
      const updated = await stepWorkflowRun(workflowId, runId);
      setRun(updated);
      const state = await getWorkflowRunState(workflowId, runId);
      setRunState(state);
    } catch {}
    setStepping(false);
  };

  const handleContinue = async () => {
    if (!workflowId || !runId) return;
    try {
      const updated = await continueWorkflowRun(workflowId, runId);
      setRun(updated);
    } catch {}
  };

  const handleAbort = async () => {
    if (!workflowId || !runId) return;
    try {
      const updated = await abortWorkflowRun(workflowId, runId);
      setRun(updated);
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-300 border-t-ink-800" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-sm font-semibold text-ink-800">Run not found</h2>
          <button onClick={() => navigate("/workflows")} className="btn-secondary mt-3 text-xs">
            Back to workflows
          </button>
        </div>
      </div>
    );
  }

  const stateStyle = STATE_STYLES[run.state] ?? STATE_STYLES.pending;
  const isActive = run.state === "running" || run.state === "pending";
  const isDebug = !!run.debugState;
  const isPaused = run.state === "paused";

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="shrink-0 border-b border-ink-100 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/workflows/${workflowId}/edit`)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-400 hover:bg-surface-2 hover:text-ink-700"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M10 4L6 8l4 4" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-semibold text-ink-900">
                {workflow?.name ?? "Workflow"} — {isDebug ? "Debug" : "Run"}
              </h1>
              <span className={stateStyle.badge}>{stateStyle.label}</span>
              {isActive && (
                <span className="flex items-center gap-1 text-2xs text-ink-400">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
                  Live
                </span>
              )}
              {isDebug && isPaused && (
                <span className="flex items-center gap-1 text-2xs text-amber-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  Debugging — Step {run.debugState!.stepIndex}
                </span>
              )}
            </div>
            <span className="font-mono text-2xs text-ink-400">{run.runId}</span>
          </div>
        </div>

        {/* Debug controls */}
        {isDebug && isPaused && (
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={handleStep}
              disabled={stepping}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <StepIcon className="h-3.5 w-3.5" />
              {stepping ? "Stepping..." : "Step"}
            </button>
            <button
              onClick={handleContinue}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
            >
              <PlayIcon className="h-3.5 w-3.5" />
              Continue
            </button>
            <button
              onClick={handleAbort}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
            >
              <StopIcon className="h-3.5 w-3.5" />
              Abort
            </button>
            {run.debugState?.nextNodeIds?.[0] && (
              <span className="ml-2 text-xs text-ink-500">
                Next: <span className="font-mono text-ink-700">{run.debugState.nextNodeIds[0]}</span>
              </span>
            )}
          </div>
        )}
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-surface-1">
        <div className="mx-auto max-w-4xl px-6 py-6 space-y-6">
          {/* Execution path breadcrumb */}
          {run.debugState?.executionPath && run.debugState.executionPath.length > 0 && (
            <section className="card p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Execution Path</h3>
              <div className="mt-3 flex flex-wrap items-center gap-1">
                {run.debugState.executionPath.map((nodeId, i) => (
                  <span key={`${nodeId}-${i}`} className="flex items-center gap-1">
                    <button
                      onClick={() => setSelectedNode(nodeId)}
                      className="rounded bg-blue-100 px-2 py-0.5 font-mono text-2xs text-blue-700 hover:bg-blue-200"
                    >
                      {nodeId}
                    </button>
                    {i < run.debugState!.executionPath.length - 1 && (
                      <span className="text-ink-300">&rarr;</span>
                    )}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Node states — visual DAG representation */}
          {run.nodeStates && Object.keys(run.nodeStates).length > 0 && (
            <section className="card p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Node States</h3>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {workflow?.definition.nodes.map((node) => {
                  const ns = run.nodeStates?.[node.id];
                  const nodeState = ns?.state ?? (run.debugState?.skippedNodeIds?.includes(node.id) ? "skipped" : "pending");
                  const colors = NODE_STATE_COLORS[nodeState] ?? NODE_STATE_COLORS.pending;
                  const isNext = run.debugState?.nextNodeIds?.includes(node.id);
                  const isCurrent = run.debugState?.currentNodeId === node.id;

                  return (
                    <button
                      key={node.id}
                      onClick={() => setSelectedNode(node.id)}
                      className={`rounded-lg border p-3 text-left transition-all ${colors} ${
                        isNext ? "ring-2 ring-blue-400 ring-offset-1" : ""
                      } ${isCurrent ? "ring-2 ring-amber-400 ring-offset-1 animate-pulse" : ""} ${
                        selectedNode === node.id ? "ring-2 ring-ink-400" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-medium">{node.label ?? node.id}</span>
                        <span className="rounded-full px-1.5 py-0.5 text-2xs font-medium">
                          {nodeState}
                        </span>
                      </div>
                      <span className="mt-0.5 text-2xs opacity-70">{node.type}</span>
                      {ns?.durationMs != null && (
                        <span className="ml-2 text-2xs opacity-70">{ns.durationMs}ms</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Variable inspector — shows selected node input/output */}
          {selectedNode && runState && (
            <section className="card p-5">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">
                  Variable Inspector — <span className="font-mono">{selectedNode}</span>
                </h3>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="text-xs text-ink-400 hover:text-ink-700"
                >
                  Close
                </button>
              </div>
              {(() => {
                const nr = runState.nodeRuns.find((n) => n.nodeId === selectedNode);
                if (!nr) {
                  return <p className="mt-3 text-xs text-ink-400">Node has not been executed yet</p>;
                }
                return (
                  <div className="mt-3 space-y-3">
                    <div>
                      <span className="text-2xs font-semibold uppercase text-ink-400">State</span>
                      <p className="mt-1 font-mono text-xs">{nr.state}</p>
                    </div>
                    {nr.output !== undefined ? (
                      <div>
                        <span className="text-2xs font-semibold uppercase text-ink-400">Output</span>
                        <pre className="mt-1 overflow-x-auto rounded-lg bg-ink-950 px-4 py-3 font-mono text-xs text-sky-300">
                          {JSON.stringify(nr.output, null, 2)}
                        </pre>
                      </div>
                    ) : null}
                    {nr.error && (
                      <div>
                        <span className="text-2xs font-semibold uppercase text-red-400">Error</span>
                        <pre className="mt-1 overflow-x-auto rounded-lg bg-red-950 px-4 py-3 font-mono text-xs text-red-300">
                          {nr.error}
                        </pre>
                      </div>
                    )}
                    {nr.durationMs != null && (
                      <div>
                        <span className="text-2xs font-semibold uppercase text-ink-400">Duration</span>
                        <p className="mt-1 font-mono text-xs">{nr.durationMs}ms</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </section>
          )}

          {/* Timeline */}
          <section className="card p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Timeline</h3>
            <div className="mt-4 space-y-0">
              <TimelineEvent
                label="Run created"
                time={run.createdAt}
                status="completed"
              />
              {run.debugState && (
                <TimelineEvent
                  label={`Debug mode — ${run.debugState.stepIndex} step${run.debugState.stepIndex !== 1 ? "s" : ""} executed`}
                  status="active"
                />
              )}
              {run.state === "completed" && (
                <TimelineEvent label="Completed" status="completed" />
              )}
              {run.state === "failed" && (
                <TimelineEvent label="Failed" status="failed" />
              )}
              {run.state === "cancelled" && (
                <TimelineEvent label="Cancelled" status="failed" />
              )}
              {isActive && !isDebug && (
                <TimelineEvent label="In progress..." status="active" />
              )}
            </div>
          </section>

          {/* Input */}
          <section className="card p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Input</h3>
            <pre className="mt-3 overflow-x-auto rounded-lg bg-ink-950 px-4 py-3 font-mono text-xs text-emerald-300">
              {JSON.stringify(run.input, null, 2)}
            </pre>
          </section>

          {/* Output */}
          {run.output && (
            <section className="card p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-400">Output</h3>
              <pre className="mt-3 overflow-x-auto rounded-lg bg-ink-950 px-4 py-3 font-mono text-xs text-sky-300">
                {JSON.stringify(run.output, null, 2)}
              </pre>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function TimelineEvent({
  label,
  time,
  status,
}: {
  label: string;
  time?: string;
  status: "completed" | "failed" | "active";
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      {/* Dot */}
      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
        {status === "active" ? (
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-blue-500" />
        ) : status === "completed" ? (
          <svg className="h-4 w-4 text-emerald-500" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3.5 8.5l3 3 6-7" />
          </svg>
        ) : (
          <svg className="h-4 w-4 text-red-500" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        )}
      </div>
      <div className="flex-1">
        <span className="text-sm text-ink-700">{label}</span>
        {time && (
          <p className="mt-0.5 text-2xs text-ink-400">
            {new Date(time).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </p>
        )}
      </div>
    </div>
  );
}

function StepIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 3v10" />
      <path d="M9 3l4 5-4 5" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M4 2.5v11l9-5.5L4 2.5z" />
    </svg>
  );
}

function StopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <rect x="3" y="3" width="10" height="10" rx="1" />
    </svg>
  );
}
